import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const requiredText = (maximum: number) => z.string().trim().min(1).max(maximum)
const optionalText = (maximum: number) => requiredText(maximum).nullable()

const residentialProfile = z.object({
  customerType: z.literal("residential"),
  values: z.object({
    householdName: requiredText(160),
    occupancyStatus: z.enum(["owner", "tenant", "other", "unknown"]),
    billingSameAsService: z.boolean(),
    occupancyDetails: optionalText(500),
    hoaRestrictions: optionalText(500),
    propertyNotes: optionalText(1000),
    servicePreferences: optionalText(500),
  }).strict(),
}).strict()

const commercialProfile = z.object({
  customerType: z.literal("commercial"),
  values: z.object({
    legalBusinessName: requiredText(200),
    dba: optionalText(160),
    industry: requiredText(120),
    apContactId: entityIdSchema,
    operatingAccessHours: optionalText(500),
    poRequired: z.boolean(),
    paymentTerms: requiredText(120),
    taxExemptCertificateMetadata: optionalText(500),
    complianceDocumentReference: optionalText(200),
  }).strict(),
}).strict()

const propertyManagerProfile = z.object({
  customerType: z.literal("property_manager"),
  values: z.object({
    managementCompanyName: requiredText(200),
    portfolioIdentifier: requiredText(120),
    billingResponsibility: z.enum(["central", "property"]),
    authorizationLimitMinor: z.number().int().nonnegative(),
    ownerApprovalRequired: z.boolean(),
    tenantNoticeRules: optionalText(500),
    invoiceGrouping: z.enum(["central", "property", "separate"]),
  }).strict(),
}).strict()

export const customerTypeProfileV1Schema = z.discriminatedUnion("customerType", [
  residentialProfile,
  commercialProfile,
  propertyManagerProfile,
])

const baseRequest = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  customerId: entityIdSchema,
  expectedVersion: z.number().int().positive(),
  branchId: branchIdSchema,
  legalEntityId: legalEntityIdSchema,
  profile: customerTypeProfileV1Schema,
}).strict()

export const customerTypeChangePreviewRequestV1Schema = baseRequest
export const customerTypeChangeApplyRequestV1Schema = baseRequest.extend({
  impactFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  typeRevisionId: entityIdSchema,
}).strict()

const retainedSchema = z.object({
  contacts: z.number().int().nonnegative(),
  locations: z.number().int().nonnegative(),
  billingProfiles: z.number().int().nonnegative(),
  branchGrants: z.number().int().nonnegative(),
}).strict()

export const customerTypeChangePreviewResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  status: z.enum(["reviewed", "conflict", "denied", "retry"]),
  correlationId: correlationIdSchema,
  customerId: entityIdSchema.nullable(),
  currentType: z.enum(["residential", "commercial", "property_manager"]).nullable(),
  targetType: z.enum(["residential", "commercial", "property_manager"]).nullable(),
  currentVersion: z.number().int().positive().nullable(),
  currentRevisionId: entityIdSchema.nullable(),
  fieldsIntroduced: z.array(z.string()).max(16),
  fieldsRetired: z.array(z.string()).max(16),
  retained: retainedSchema.nullable(),
  historicalRecordsRetained: z.boolean(),
  impactFingerprint: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
}).strict()

export const customerTypeChangeApplyResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  status: z.enum(["confirmed", "conflict", "denied", "retry"]),
  correlationId: correlationIdSchema,
  customerId: entityIdSchema.nullable(),
  previousType: z.enum(["residential", "commercial", "property_manager"]).nullable(),
  customerType: z.enum(["residential", "commercial", "property_manager"]).nullable(),
  typeRevisionId: entityIdSchema.nullable(),
  version: z.number().int().positive().nullable(),
  historicalRecordsRetained: z.boolean(),
}).strict()

export type CustomerTypeProfileV1 = z.infer<typeof customerTypeProfileV1Schema>
export type CustomerTypeChangePreviewRequestV1 = z.infer<
  typeof customerTypeChangePreviewRequestV1Schema
>
export type CustomerTypeChangeApplyRequestV1 = z.infer<
  typeof customerTypeChangeApplyRequestV1Schema
>
export type CustomerTypeChangePreviewResponseV1 = z.infer<
  typeof customerTypeChangePreviewResponseV1Schema
>
export type CustomerTypeChangeApplyResponseV1 = z.infer<
  typeof customerTypeChangeApplyResponseV1Schema
>
